import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { formatMonth, formatNumber } from '../../lib/format.js';
import { AnalyticsPanel } from './analytics-panel.js';
import { ChangeFigure, CompactMoney } from './figures.js';
import { percentChange, type MonthRow } from './sales-series.js';

/**
 * The last six months as a plain table, newest first — the panel for the owner who would rather
 * read figures than shapes, and the one that answers "was last month better than the one
 * before?" without anybody measuring bar heights.
 *
 * The change column compares each month with the one before it. The month in progress is
 * compared with the same days of last month rather than all of it (see `monthToDate`), so its
 * change matches the "Sales this month" card instead of contradicting it. The oldest month has
 * nothing before it in the data Home fetched, and says so with a dash rather than inventing a
 * figure; a dash also stands in for a month after one with no sales, where a percentage would be
 * a division by zero.
 */
export function MonthlySalesTable({
  months,
  className,
}: {
  months: readonly MonthRow[];
  className?: string | undefined;
}): ReactElement {
  const { t } = useTranslation();
  const title = t('dashboard.analytics.monthByMonth');
  const newestFirst = [...months].reverse();

  return (
    <AnalyticsPanel title={title} className={className}>
      <table className="w-full text-sm">
        <caption className="sr-only">{title}</caption>
        <thead>
          <tr className="text-left text-xs text-muted-foreground">
            <th scope="col" className="py-1 font-medium">
              {t('dashboard.analytics.month')}
            </th>
            <th scope="col" className="py-1 text-right font-medium">
              {t('dashboard.analytics.sales')}
            </th>
            <th scope="col" className="py-1 text-right font-medium">
              {t('dashboard.analytics.orders')}
            </th>
            <th scope="col" className="py-1 text-right font-medium">
              {t('dashboard.analytics.change')}
            </th>
          </tr>
        </thead>
        <tbody>
          {newestFirst.map((row) => (
            <tr key={row.month} data-month={row.month} className="border-t border-border">
              <th scope="row" className="py-2 text-left font-normal">
                {row.partial
                  ? t('dashboard.analytics.monthSoFar', { month: formatMonth(row.month, 'long') })
                  : formatMonth(row.month, 'long')}
              </th>
              <td className="py-2 text-right font-medium tabular-nums">
                <CompactMoney paise={row.revenuePaise} />
              </td>
              <td className="py-2 text-right tabular-nums">{formatNumber(row.orderCount)}</td>
              <td className="py-2 text-right text-xs">
                <ChangeFigure
                  glyph="triangle"
                  change={percentChange(row.revenuePaise, row.previousRevenuePaise)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </AnalyticsPanel>
  );
}
